import { useCallback, useEffect, useRef, useState } from "react";
import {
  ReactFlow, ReactFlowProvider, Background, Controls,
  useNodesState, useEdgesState, addEdge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Plus, Trash } from "@phosphor-icons/react";
import {
  BusNode, edgeStyle, NODE_STYLE, SidePanel, Toolbar, ToolbarBtn, ToolbarDivider,
} from "./flow/primitives";

/* v3.2 · Multi-machine transient-stability graphical editor.
   Nodes: BUS (network node with load), MACHINE (generator with H/X'd/Pm/E'),
          FAULT (a bus flagged as fault location). Edges are transmission
          lines with R/X per-unit and (optionally) a `tripped` flag that
          removes them in the post-fault network.

   Payload out (graphToTS):
     { buses:[{id,kV,load_p_pu,load_q_pu}],
       lines:[{from_bus,to_bus,R_pu,X_pu,B_pu}],
       machines:[{tag,bus_id,H_s,Xd_prime_pu,Pm_pu,E_pu}],
       fault_bus_id, tripped_line, fault_clearing_s } */

const Bus     = (p) => <BusNode {...p} {...NODE_STYLE.NBUS}
  sub={(d) => `${d.kV} kV · load ${d.load_p_pu ?? 0}+${d.load_q_pu ?? 0}j pu`} />;
const Machine = (p) => <BusNode {...p} {...NODE_STYLE.MACHINE}
  sub={(d) => `H=${d.H_s}s · X'd=${d.Xd_prime_pu} · Pm=${d.Pm_pu}pu`} />;
const Fault   = (p) => <BusNode {...p} {...NODE_STYLE.FAULT}
  sub={(d) => `fault @ ${d.fault_bus_id || "—"} · clear ${d.fault_clearing_s}s`} />;

const nodeTypes = { NBUS: Bus, MACHINE: Machine, FAULT: Fault };

const INITIAL_NODES = [
  { id: "B1", type: "NBUS", position: { x:  60, y: 200 },
    data: { id: "B1", tag: "Bus 1", kV: 13.8, load_p_pu: 0, load_q_pu: 0 } },
  { id: "B2", type: "NBUS", position: { x: 320, y: 200 },
    data: { id: "B2", tag: "Bus 2", kV: 13.8, load_p_pu: 0.5, load_q_pu: 0.1 } },
  { id: "B3", type: "NBUS", position: { x: 580, y: 200 },
    data: { id: "B3", tag: "Bus 3", kV: 13.8, load_p_pu: 0.3, load_q_pu: 0.05 } },
  { id: "M1", type: "MACHINE", position: { x:  60, y: 60 },
    data: { id: "M1", tag: "G1", bus_id: "B1", H_s: 5.0, Xd_prime_pu: 0.20, Pm_pu: 0.6, E_pu: 1.05 } },
  { id: "M2", type: "MACHINE", position: { x: 580, y: 60 },
    data: { id: "M2", tag: "G2", bus_id: "B3", H_s: 3.0, Xd_prime_pu: 0.25, Pm_pu: 0.4, E_pu: 1.03 } },
  { id: "FLT", type: "FAULT", position: { x: 320, y: 360 },
    data: { id: "FLT", tag: "Fault", fault_bus_id: "B2", fault_clearing_s: 0.15,
             tripped_line: { from_bus: "B2", to_bus: "B3" } } },
];
const INITIAL_EDGES = [
  { id: "l1", source: "B1", target: "B2", ...edgeStyle(),
    data: { R_pu: 0.01, X_pu: 0.08, B_pu: 0, tripped: false }, label: "R=.01 X=.08" },
  { id: "l2", source: "B2", target: "B3", ...edgeStyle(),
    data: { R_pu: 0.01, X_pu: 0.08, B_pu: 0, tripped: true },  label: "TRIPPED" },
  { id: "l3", source: "B1", target: "B3", ...edgeStyle(),
    data: { R_pu: 0.01, X_pu: 0.10, B_pu: 0, tripped: false }, label: "R=.01 X=.10" },
  { id: "mln1", source: "M1", target: "B1", ...edgeStyle(),
    animated: true, style: { stroke: "#7CFF6B", strokeWidth: 1.2, strokeDasharray: "3 3" } },
  { id: "mln2", source: "M2", target: "B3", ...edgeStyle(),
    animated: true, style: { stroke: "#7CFF6B", strokeWidth: 1.2, strokeDasharray: "3 3" } },
];

const FIELDS = {
  NBUS: [
    { key: "tag",       label: "Tag",             type: "text" },
    { key: "kV",        label: "Nominal kV",      type: "number", step: "0.1" },
    { key: "load_p_pu", label: "Load P (pu)",     type: "number", step: "0.05" },
    { key: "load_q_pu", label: "Load Q (pu)",     type: "number", step: "0.05" },
  ],
  MACHINE: [
    { key: "tag",         label: "Tag",              type: "text" },
    { key: "bus_id",      label: "Bus id",           type: "text" },
    { key: "H_s",         label: "Inertia H (s)",    type: "number", step: "0.5" },
    { key: "Xd_prime_pu", label: "X'd (pu)",         type: "number", step: "0.05" },
    { key: "Pm_pu",       label: "Pm (pu)",          type: "number", step: "0.05" },
    { key: "E_pu",        label: "E' (pu)",          type: "number", step: "0.01" },
    { key: "avr_enabled",     label: "AVR",       type: "select",
      options: [{ value: "off", label: "off" }, { value: "on", label: "on (Ka=30, Ta=0.05, Vref=1.0)" }] },
    { key: "governor_enabled", label: "Governor", type: "select",
      options: [{ value: "off", label: "off" }, { value: "on", label: "on (R=0.05, Tg=0.5)" }] },
  ],
  FAULT: [
    { key: "fault_bus_id",     label: "Fault bus id",       type: "text" },
    { key: "fault_clearing_s", label: "Clearing time (s)",  type: "number", step: "0.01" },
  ],
};

const LINE_FIELDS = [
  { key: "R_pu",     label: "R (pu)",     type: "number", step: "0.005" },
  { key: "X_pu",     label: "X (pu)",     type: "number", step: "0.01" },
  { key: "B_pu",     label: "B (pu)",     type: "number", step: "0.01" },
  { key: "tripped",  label: "Trip on fault",  type: "select",
    options: [{ value: "false", label: "no" }, { value: "true", label: "yes" }] },
];

/* Serialise the graph into the /api/studies/transient-stability payload. */
export function graphToTS(nodes, edges) {
  const buses    = nodes.filter((n) => n.type === "NBUS").map((n) => ({
    id: n.data.id, kV: Number(n.data.kV || 13.8),
    load_p_pu: Number(n.data.load_p_pu || 0),
    load_q_pu: Number(n.data.load_q_pu || 0),
  }));
  const machines = nodes.filter((n) => n.type === "MACHINE").map((n) => {
    const m = {
      tag: n.data.tag || n.data.id,
      bus_id: n.data.bus_id, H_s: Number(n.data.H_s || 4),
      Xd_prime_pu: Number(n.data.Xd_prime_pu || 0.2),
      Pm_pu: Number(n.data.Pm_pu || 0.6),
      E_pu: Number(n.data.E_pu || 1.05),
    };
    // v3.2.2 · AVR / governor toggles from the side panel.
    if (n.data.avr_enabled === "on" || n.data.avr_enabled === true) {
      m.avr = { Ka: 30, Ta: 0.05, Vref: 1.0 };
    }
    if (n.data.governor_enabled === "on" || n.data.governor_enabled === true) {
      m.governor = { R_droop: 0.05, Tg: 0.5 };
    }
    return m;
  });
  const busIds = new Set(buses.map((b) => b.id));
  const lines = edges.filter((e) => busIds.has(e.source) && busIds.has(e.target)).map((e) => ({
    from_bus: e.source, to_bus: e.target,
    R_pu: Number(e.data?.R_pu ?? 0.01),
    X_pu: Number(e.data?.X_pu ?? 0.08),
    B_pu: Number(e.data?.B_pu ?? 0),
  }));
  const trippedEdge = edges.find((e) => e.data?.tripped
                                     && busIds.has(e.source) && busIds.has(e.target));
  const fault = nodes.find((n) => n.type === "FAULT")?.data || {};
  return {
    buses, lines, machines,
    fault_bus_id:     fault.fault_bus_id || null,
    fault_clearing_s: Number(fault.fault_clearing_s || 0.15),
    tripped_line:     trippedEdge
      ? { from_bus: trippedEdge.source, to_bus: trippedEdge.target } : null,
    total_time_s:     2.0,
    step_s:           0.005,
  };
}

function MultiMachineSLDInner({ onChange }) {
  const [nodes, setNodes, onNodesChange] = useNodesState(INITIAL_NODES);
  const [edges, setEdges, onEdgesChange] = useEdgesState(INITIAL_EDGES);
  const [sel, setSel] = useState({ node: null, edge: null });
  const idCounter = useRef(10);

  useEffect(() => { onChange?.(graphToTS(nodes, edges)); }, [nodes, edges, onChange]);

  const onConnect = useCallback((c) => {
    if (c.source === c.target) return;
    setEdges((es) => addEdge({
      ...c, id: `l${idCounter.current++}`, ...edgeStyle(),
      data: { R_pu: 0.01, X_pu: 0.08, B_pu: 0, tripped: false },
      label: "R=.01 X=.08",
    }, es));
  }, [setEdges]);

  const addNode = (type) => {
    const id = `${type[0]}${idCounter.current++}`;
    const defaults = type === "NBUS"
      ? { tag: id, kV: 13.8, load_p_pu: 0, load_q_pu: 0 }
      : type === "MACHINE"
      ? { tag: id, bus_id: "B1", H_s: 4, Xd_prime_pu: 0.2, Pm_pu: 0.5, E_pu: 1.05 }
      : { fault_bus_id: "B2", fault_clearing_s: 0.15 };
    setNodes((ns) => [...ns, {
      id, type,
      position: { x: 120 + Math.random() * 400, y: 60 + Math.random() * 260 },
      data: { id, ...defaults },
    }]);
  };

  const clearAll = () => { setNodes([]); setEdges([]); setSel({ node: null, edge: null }); };
  const onSelectionChange = useCallback(({ nodes: ns, edges: es }) =>
    setSel({ node: ns[0] || null, edge: es[0] || null }), []);

  const patchNode = (key, value) => {
    if (!sel.node) return;
    setNodes((all) => all.map((n) => n.id === sel.node.id
      ? { ...n, data: { ...n.data, [key]: value } } : n));
    setSel((s) => ({ ...s, node: { ...s.node, data: { ...s.node.data, [key]: value } } }));
  };
  const patchEdge = (key, value) => {
    if (!sel.edge) return;
    const parsed = key === "tripped"
      ? (value === true || value === "true") : Number(value);
    setEdges((all) => all.map((e) => e.id === sel.edge.id
      ? { ...e, data: { ...(e.data || {}), [key]: parsed },
          label: parsed && key === "tripped" ? "TRIPPED"
                 : `R=${e.data?.R_pu} X=${e.data?.X_pu}` }
      : e));
    setSel((s) => ({ ...s, edge: { ...s.edge, data: { ...(s.edge.data || {}), [key]: parsed } } }));
  };
  const deleteSel = () => {
    if (sel.node) {
      const id = sel.node.id;
      setNodes((ns) => ns.filter((n) => n.id !== id));
      setEdges((es) => es.filter((e) => e.source !== id && e.target !== id));
    } else if (sel.edge) {
      setEdges((es) => es.filter((e) => e.id !== sel.edge.id));
    }
    setSel({ node: null, edge: null });
  };

  return (
    <div className="h-[520px] border border-white/10 bg-[#0A0C10] relative" data-testid="ts-sld-canvas">
      <Toolbar>
        <ToolbarBtn colour="#00E5FF" onClick={() => addNode("NBUS")}
          testid="ts-add-bus" title="Add bus">
          <Plus size={10} className="inline" /> bus
        </ToolbarBtn>
        <ToolbarBtn colour="#7CFF6B" onClick={() => addNode("MACHINE")}
          testid="ts-add-machine" title="Add machine (generator)">
          <Plus size={10} className="inline" /> machine
        </ToolbarBtn>
        <ToolbarBtn colour="#FF3B30" onClick={() => addNode("FAULT")}
          testid="ts-add-fault" title="Add fault marker">
          <Plus size={10} className="inline" /> fault
        </ToolbarBtn>
        <ToolbarDivider />
        <ToolbarBtn colour="#FF3B30" onClick={deleteSel}
          testid="ts-delete-selected" title="Delete selected">
          <Trash size={10} className="inline" /> delete
        </ToolbarBtn>
        <ToolbarBtn colour="#8792A2" onClick={clearAll}
          testid="ts-clear" title="Reset">reset</ToolbarBtn>
      </Toolbar>

      <ReactFlow
        nodes={nodes} edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onSelectionChange={onSelectionChange}
        fitView
        proOptions={{ hideAttribution: true }}
        colorMode="dark"
      >
        <Background gap={16} color="#ffffff08" />
        <Controls className="!bg-[#12151C] !border-white/10" showInteractive={false} />
      </ReactFlow>

      {sel.node && (
        <SidePanel
          title={sel.node.data.tag || sel.node.data.id}
          subtitle={sel.node.type.toLowerCase()}
          node={sel.node}
          fields={FIELDS[sel.node.type] || []}
          onChange={patchNode}
          onDelete={deleteSel}
          onClose={() => setSel({ node: null, edge: null })}
        />
      )}
      {sel.edge && !sel.node && (
        <SidePanel
          title={`${sel.edge.source} → ${sel.edge.target}`}
          subtitle="transmission line"
          edge={sel.edge}
          fields={LINE_FIELDS}
          onChange={patchEdge}
          onDelete={deleteSel}
          onClose={() => setSel({ node: null, edge: null })}
        />
      )}
    </div>
  );
}

export default function MultiMachineSLD({ onChange }) {
  return (
    <ReactFlowProvider>
      <MultiMachineSLDInner onChange={onChange} />
    </ReactFlowProvider>
  );
}
